/**
 * Chat attachments for Ask Cannafax: files are read in the browser into plain
 * text and sent with that one question. Nothing is uploaded or stored; only the
 * file names are kept in the chat history.
 */

export interface ChatAttachment {
  id: string;
  name: string;
  size: number;
  kind: 'spreadsheet' | 'pdf' | 'text';
  text: string;
  truncated: boolean;
  /** Original file, kept in memory only so a confirmed chat import can upload it. */
  file?: File;
}

// Server enforces the same caps (supabase/functions/ask-cannafax/attachments.ts).
export const MAX_ATTACHMENTS = 3;
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
export const MAX_ATTACHMENT_CHARS = 40_000;

export const ACCEPTED_ATTACHMENT_TYPES = '.csv,.tsv,.xlsx,.pdf,.txt,.md';

export function attachmentKind(name: string): ChatAttachment['kind'] | null {
  const ext = name.toLowerCase().split('.').pop() ?? '';
  if (ext === 'xlsx' || ext === 'csv' || ext === 'tsv') return 'spreadsheet';
  if (ext === 'pdf') return 'pdf';
  if (ext === 'txt' || ext === 'md') return 'text';
  return null;
}

export function clampAttachmentText(text: string): { text: string; truncated: boolean } {
  const clean = text.replace(/\u0000/g, '').trim();
  if (clean.length <= MAX_ATTACHMENT_CHARS) return { text: clean, truncated: false };
  return { text: clean.slice(0, MAX_ATTACHMENT_CHARS), truncated: true };
}

// Shared with the Google Drive reader (ask-cannafax driveTool) so uploaded
// workbooks and Drive workbooks are read identically: every tab announced and
// given a fair share of the character budget.
import { budgetSheetParts } from '../../supabase/functions/ask-cannafax/attachments';

async function readXlsx(file: File): Promise<string> {
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(await file.arrayBuffer());
  const sheets = wb.worksheets.filter(Boolean);
  const parts = sheets.map((sheet) => {
    const rows: string[] = [];
    sheet.eachRow({ includeEmpty: false }, (row) => {
      const values = (row.values as unknown[]).slice(1).map((v) => {
        if (v == null) return '';
        if (v instanceof Date) return v.toISOString().slice(0, 10);
        if (typeof v === 'object') {
          const o = v as { text?: string; result?: unknown; richText?: { text: string }[] };
          if (o.richText) return o.richText.map((r) => r.text).join('');
          if (o.text != null) return String(o.text);
          if (o.result != null) return String(o.result);
          return '';
        }
        return String(v);
      });
      rows.push(values.map((s) => s.replace(/[\t\n]+/g, ' ')).join('\t'));
    });
    return rows.join('\n');
  });
  if (!sheets.length) return '';
  // Every tab is announced in a table of contents and gets a fair slice of the
  // character budget, so a big first tab can't hide later tabs. Reserve room
  // for the TOC header so the final clamp can never clip the last tab.
  const tocReserve = 40 + sheets.reduce((s, sh) => s + sh.name.length + 2, 0) + sheets.length * 16;
  const { parts: budgeted, truncatedTabs } = budgetSheetParts(parts, MAX_ATTACHMENT_CHARS, tocReserve);
  const toc = `# Workbook tabs (${sheets.length}): ` + sheets
    .map((sheet, i) =>
      !parts[i].trim() ? `${sheet.name} (empty)` : truncatedTabs[i] ? `${sheet.name} (partly read)` : sheet.name
    )
    .join(', ');
  return toc + '\n' + budgeted.map((t, i) => `# Sheet: ${sheets[i].name}\n${t}`).join('\n');
}

async function readPdf(file: File): Promise<string> {
  const pdfjs = await import('pdfjs-dist');
  const worker = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  const doc = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
  const pages: string[] = [];
  for (let i = 1; i <= Math.min(doc.numPages, 50); i++) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    pages.push(content.items.map((it) => ('str' in it ? it.str : '')).join(' '));
  }
  return pages.join('\n\n');
}

export async function readAttachment(file: File): Promise<ChatAttachment> {
  const kind = attachmentKind(file.name);
  if (!kind) {
    if (/\.(png|jpe?g|gif|webp|heic|bmp|tiff?)$/i.test(file.name)) {
      throw new Error(`I can't read pictures yet — for ${file.name}, send the spreadsheet itself (Excel or CSV) or a PDF, or just type the details.`);
    }
    throw new Error(`${file.name} isn't a supported file. Try a spreadsheet, PDF or text file.`);
  }
  if (file.size > MAX_ATTACHMENT_BYTES) throw new Error(`${file.name} is larger than 10 MB.`);
  let raw: string;
  if (kind === 'pdf') raw = await readPdf(file);
  else if (file.name.toLowerCase().endsWith('.xlsx')) raw = await readXlsx(file);
  else raw = await file.text();
  if (!raw.trim()) {
    throw new Error(
      kind === 'pdf'
        ? `${file.name} has no readable text (it may be a scanned image).`
        : `${file.name} is empty.`,
    );
  }
  const { text, truncated } = clampAttachmentText(raw);
  return { id: crypto.randomUUID(), name: file.name, size: file.size, kind, text, truncated, file };
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
